/**
 * Stateful base64 PCM16 decoder that carries an unpaired trailing byte across chunks.
 */
export class PCM16Decoder {
  /** Low byte of a sample split across packet boundaries, if any. */
  private pendingByte: number | null = null;

  /**
   * Decodes a base64 PCM16 chunk into float32 samples in [-1, 1].
   * Incomplete trailing bytes are retained until the next chunk completes them.
   */
  public decode(chunkBase64: string): Float32Array<ArrayBuffer> {
    const binary = atob(chunkBase64);
    const incomingLength = binary.length;

    // An empty chunk carries nothing to pair a pending byte with: keep it for the
    // next chunk instead of dropping it and mis-framing the rest of the stream.
    if (incomingLength === 0) {
      return new Float32Array(0);
    }

    const pending = this.pendingByte;
    const totalLength = incomingLength + (pending === null ? 0 : 1);

    const consumableLength = totalLength - (totalLength % 2);
    const bytes = new Uint8Array(consumableLength);

    let writeOffset = 0;

    // If there is a pending byte, write it to the beginning of the bytes array
    if (pending !== null) {
      bytes[0] = pending;
      writeOffset = 1;
      this.pendingByte = null;
    }

    const completeIncomingLength = consumableLength - writeOffset;

    for (let i = 0; i < completeIncomingLength; i += 1) {
      bytes[writeOffset + i] = binary.charCodeAt(i);
    }

    if (consumableLength < totalLength) {
      this.pendingByte = binary.charCodeAt(incomingLength - 1);
    }

    if (consumableLength === 0) {
      return new Float32Array(0);
    }

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const samples = new Float32Array(consumableLength / 2);

    for (let i = 0; i < samples.length; i += 1) {
      const sample = view.getInt16(i * 2, true);

      samples[i] = sample < 0 ? sample / 32768 : sample / 32767;
    }

    return samples;
  }

  /**
   * Drops any unpaired byte so the next stream does not inherit framing from a prior turn.
   */
  public reset(): void {
    this.pendingByte = null;
  }
}
